"use client"

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker"
import dayjs, { type Dayjs } from "dayjs"
import "dayjs/locale/he"
import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form"
import ArrowDown from "../../../icons/ArrowDown"
import { colors } from "../../../theme/colors"
import { pillFieldSx } from "./FormTextField"

/* EGDE-449: the Figma time picker is a single rounded list of half-hour
   options where the chosen time is an orange gradient pill — style the
   dropdown paper, its options and the confirm button to match. */
const timePaperSx = {
  borderRadius: "16px",
  boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.12)",
  /* The theme paints Paper surfaces grey (`background.paper`) — the design's
     dropdown is a white card. */
  backgroundColor: colors.primitives.neutral0,
  "& .MuiDigitalClock-root": {
    maxHeight: 240,
    paddingInline: "8px",
  },
  "& .MuiDigitalClock-item": {
    borderRadius: "9999px",
    fontSize: 14,
    minHeight: 32,
    paddingBlock: "6px",
    justifyContent: "center",
    "&.Mui-selected": {
      background: colors.gradients.gradientCta,
      color: colors.primitives.neutral0,
      "&:hover, &:focus": {
        background: colors.gradients.gradientCta,
      },
    },
  },
  "& .MuiDialogActions-root .MuiButton-root": {
    background: colors.gradients.gradientCta,
    color: colors.primitives.neutral0,
    borderRadius: "9999px",
    paddingInline: "24px",
    fontWeight: 700,
  },
} as const

/* The form stores time as an "HH:mm" string (matches the backend multipart
   field and regex validation). We bridge to MUI's Dayjs-based TimePicker here
   so the rest of the form never deals with date objects. */
function stringToDayjs(value: unknown): Dayjs | null {
  if (typeof value !== "string" || !/^\d{1,2}:\d{2}$/.test(value)) return null
  const parsed = dayjs(`2000-01-01T${value.padStart(5, "0")}`)
  return parsed.isValid() ? parsed : null
}

interface FormTimePickerProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label: string
  locale: string
  required?: boolean
  error?: string
}

export default function FormTimePicker<T extends FieldValues>({
  control,
  name,
  label,
  locale,
  required,
  error,
}: FormTimePickerProps<T>) {
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={locale === "en" ? "en" : "he"}
    >
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          /* EGDE-259: the responsive TimePicker switches to MobileTimePicker on
             touch devices — a clock-dial modal whose OK/Cancel staging buffer
             desynced from our "HH:mm" string bridge, leaving the readout stuck
             at "--:--". DesktopTimePicker gives the same typed field + dropdown
             on every viewport and commits straight through onChange. */
          <DesktopTimePicker
            label={label}
            value={stringToDayjs(field.value)}
            onChange={(value: Dayjs | null) =>
              field.onChange(value && value.isValid() ? value.format("HH:mm") : "")
            }
            ampm={false}
            format="HH:mm"
            /* EGDE-449: one flat list of half-hour options (00:00, 00:30, …)
               like the design, instead of separate hour/minute columns. The
               48 options are under the single-column threshold. */
            timeSteps={{ hours: 1, minutes: 30 }}
            thresholdToRenderTimeInASingleColumn={96}
            slots={{ openPickerIcon: ArrowDown }}
            slotProps={{
              desktopPaper: { sx: timePaperSx },
              openPickerIcon: { width: 16, height: 8 },
              textField: {
                fullWidth: true,
                size: "small",
                required,
                error: !!error,
                helperText: error,
                onBlur: field.onBlur,
                sx: pillFieldSx,
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  )
}
